import {
    eq,
    filter,
    find,
    flatten,
    get,
    getOr,
    includes,
    isEmpty,
    isNil,
    map,
    maxBy,
    pipe,
    values,
    uniqWith,
    isEqual
} from 'lodash/fp';
import { createSelector } from '@reduxjs/toolkit';
import { Defaults, ErrorLevel } from 'app/lib/constants';
import { getCurrentProjectSlug } from 'app/routes';
import { hasDifferentSchema, hasStaleData, resolveIds } from 'app/store/utils';
import { findViewBySpaceAndFqn } from 'app/lib/utils';
import { selectEntitiesById, selectEntityIdsBySlug } from 'app/store/entities/selectors.js';
import { selectCurrentUserName } from 'app/store/users/selectors.js';
import { isErrorInReference } from 'app/lib/error';
import { selectEnvironmentForPropEnvUid } from 'app/store/jobs/selectors';
import { CreateProjectState } from './constants';
import { getDatasetErrors } from './utils';

export const selectSlice = get('projects');

const selectPropEntityId = (_, props) => props.entityId;

export const selectIsLoadingProjects = createSelector(selectSlice, get('isLoading'));

export const selectUpstreamDataSourceFqnsByViewUid = createSelector(
    selectSlice,
    get('currentProjectUpstreamDataSourceFqnsByViewUid')
);
export const selectAllUpstreamSourceFqns = createSelector(
    selectUpstreamDataSourceFqnsByViewUid,
    (upstreamSources) => {
        const upstreamSourcesValues = pipe(values, flatten)(upstreamSources);
        const uniqueVals = uniqWith(isEqual, upstreamSourcesValues);
        return uniqueVals;
    }
);

export const selectCurrentProjectId = createSelector(
    [getCurrentProjectSlug, selectEntityIdsBySlug],
    getOr(null)
);

export const selectProjects = createSelector(selectSlice, get('all'));

export const selectProject = createSelector(
    selectCurrentProjectId,
    selectEntitiesById,
    getOr(null)
);

export const selectIsUserProjectOwner = createSelector(
    selectProject,
    selectCurrentUserName,
    (project, username) => project?.createdBy === username
);

function getActualTypeForEntity(entity) {
    switch (entity._type) {
        case 'europa.space.ui.TransformationStackDto': {
            // We can't get a single type for legacy Transformation Stacks
            // with more than one operation
            if (entity.operationUids.length > 1) {
                return null;
            }
            const [opId] = entity.operationUids;
            return entity.transformationsByUid[opId]?._type;
        }
        case 'europa.space.transformation.GenericTransform':
            return entity.payload.type;
        default:
            return entity._type;
    }
}

const HUMAN_READABLE_TYPES = {
    AggregationDto: 'Aggregate',
    DataSourceReferenceDto: 'Datasource',
    Combine: 'Union',
    EXTRACT_JSON: 'Extract JSON',
    FILTER: 'Filter',
    FormulaDto: 'Formula',
    Join: 'Join',
    MULTI_JOIN: 'Multi-Join',
    PivotDto: 'Pivot',
    QueryBuilder: 'Formula Builder',
    ReplaceDto: 'Replace',
    SplitDto: 'Split',
    SqlViewDto: 'SQL',
    SubstringExtractDto: 'Extract'
};

function getHumanReadableTypeForEntity(entity) {
    const actualType = getActualTypeForEntity(entity);
    if (isNil(actualType)) {
        return null;
    }
    const type = actualType.split('.').at(-1);
    return HUMAN_READABLE_TYPES[type];
}

export const selectProjectSearchData = createSelector(
    [selectProject, selectEntitiesById],
    (project, entitiesById) => {
        if (!project) {
            return null;
        }

        const { entityId, groupIds, viewUuids } = project;
        const groupIdByEntityId = {};

        for (const groupId of groupIds) {
            const groupUid = `${entityId}.${groupId}`;
            const group = entitiesById[groupUid];
            for (const childId of group.children) {
                groupIdByEntityId[`${entityId}.${childId}`] = groupUid;
            }
        }

        return viewUuids.map((id) => {
            const entity = entitiesById[id];

            return {
                entityId: id,
                name: entity.name,
                uid: entity.uid,
                type: getHumanReadableTypeForEntity(entity),
                groupId: groupIdByEntityId[id],
                columns: entity.columnIds?.map((id) => entitiesById[id]?.name) ?? []
            };
        });
    }
);

// duplicate of selectCurrentProjectId???
export const selectProjectUid = createSelector(selectProject, get('uid'));

export const selectProjectSlug = createSelector(selectProject, get('slug'));

export const selectCurrentProjectVersion = createSelector(selectProject, get('version'));

export const selectProjectEnv = createSelector(selectProject, get('envRef'));
export const selectProjectEnvUid = createSelector(selectProject, get('envRef.uid'));

const selectState = (state) => state;
export const selectProjectEnvRole = createSelector(
    selectProjectEnvUid,
    selectState,
    (projectEnvUid, state) => {
        const environment = selectEnvironmentForPropEnvUid(state, { envUid: projectEnvUid });
        return environment?.role;
    }
);

export const selectIsSettingProjectEnv = createSelector(selectSlice, get('isSettingProjectEnv'));

export const selectProjectId = createSelector(selectProject, get('id'));

export const selectProjectByPropId = createSelector(
    [selectProjects, (_, props) => props.entityId],
    (projects, entityId) => find({ entityId }, projects) || null
);

export const selectIsProjectDeletableByPropId = createSelector(
    selectProjectByPropId,
    get('isDeletable')
);

export const selectIsProjectOwnerModifiableByPropId = createSelector(
    selectProjectByPropId,
    get('isOwnerModifiable')
);

export const selectProjectSlugByEntityId = createSelector(selectProjectByPropId, get('slug'));

const selectCreateProjectState = createSelector(selectSlice, get('createProjectState'));

export const selectIsCreatingProject = createSelector(
    selectCreateProjectState,
    eq(CreateProjectState.IN_PROGRESS)
);

export const selectIsCreateProjectDialogOpen = createSelector(selectCreateProjectState, (status) =>
    includes(status, [CreateProjectState.DIALOG_OPEN, CreateProjectState.IN_PROGRESS])
);

export const selectObjectToImportToCreatedProject = createSelector(
    selectSlice,
    get('objectToImportToCreatedProject')
);

export const selectIsAddingExplorations = createSelector(selectSlice, get('isAddingExplorations'));

export const selectProjectTransformationIds = createSelector(
    selectProject,
    getOr(Defaults.ARRAY, 'viewUuids')
);

export const selectDependencies = createSelector(
    selectProject,
    getOr(Defaults.ARRAY, 'dependencies')
);

export const selectErrors = createSelector(selectProject, get('errors'));
export const selectWarnings = createSelector(selectProject, get('warnings'));

export const setErrors = (view, errors) => {
    let operationErrors = view.operationUids?.map((uid) => errors[uid]).filter((err) => err);

    if ((!operationErrors || operationErrors.length === 0) && errors?.[view.uid]) {
        operationErrors = [errors[view.uid]];
    }

    if (operationErrors?.length > 0) {
        const hasReferenceError = operationErrors.some(isErrorInReference);

        return {
            ...view,
            error: {
                level: ErrorLevel.ERROR,
                isReferenceError: hasReferenceError
            }
        };
    }

    return view;
};

export const setDeployedFrom = (view, entities) => {
    const { deployedFrom, ...transformation } = view;

    if (isNil(deployedFrom)) {
        return transformation;
    }

    const deployedFromTransformation = entities[deployedFrom];

    if (
        isNil(deployedFromTransformation) ||
        deployedFromTransformation.spaceUid !== view.spaceUid
    ) {
        return transformation;
    }

    return {
        ...transformation,
        deployedFrom: deployedFromTransformation,
        hasSchemaChanged: hasDifferentSchema(transformation),
        hasDataChanged: hasStaleData(transformation, deployedFromTransformation)
    };
};

export const selectRawTransformationsInCurrentProject = createSelector(
    selectEntitiesById,
    selectProjectTransformationIds,
    resolveIds
);

export const selectTransformationsInCurrentProject = createSelector(
    selectRawTransformationsInCurrentProject,
    selectErrors,
    selectEntitiesById,
    (views, errors, entities) =>
        views.map((view) => setErrors(view, errors)).map((view) => setDeployedFrom(view, entities))
);

const selectMostRecentTransformation = createSelector(
    selectTransformationsInCurrentProject,
    maxBy('modifiedAt')
);

export const selectSelectedEntityId = createSelector(
    selectSlice,
    selectCurrentProjectId,
    (slice, projectUid) => getOr(null, ['currentEntityIdByProjectId', projectUid], slice)
);

const selectViewBySelectedEntityId = createSelector(
    selectSelectedEntityId,
    selectEntitiesById,
    (entityId, entitiesById) => (isNil(entityId) ? null : entitiesById[entityId])
);

export const selectSelectedOrMostRecentView = createSelector(
    selectViewBySelectedEntityId,
    selectMostRecentTransformation,
    (selectedTransformation, mostRecentTransformation) =>
        selectedTransformation || mostRecentTransformation
);

export const selectSelectedEntityFqn = createSelector(
    selectViewBySelectedEntityId,
    get('tableFqn')
);

export const selectSelectedOrMostRecentViewUid = createSelector(
    selectSelectedOrMostRecentView,
    get('uid')
);

export const selectProjectHasAssets = createSelector(
    selectProject,
    (project) => !isEmpty(project?.viewUids)
);

export const selectProjectAssets = createSelector(
    selectProject,
    selectEntitiesById,
    (project, entitiesById) => {
        if (!project) {
            return Defaults.ARRAY;
        }

        const viewIds = filter((viewId) => entitiesById[viewId], project.viewUuids);

        return map((viewId) => {
            const view = entitiesById[viewId];
            const schema = project.schemasByUid[view.uid]
                ? map(
                      (column) => ({
                          entityId: column.entityId,
                          name: column.name,
                          type: column.typeFamily
                      }),
                      project.schemasByUid[view.uid].columns
                  )
                : [];

            return {
                entityId: viewId,
                name: view.name,
                slug: view.slug,
                type: view.payload?.type ?? view._type,
                schema,
                error: getDatasetErrors(view.uid, project.errors)
            };
        }, viewIds);
    }
);

export const selectCloneProjectProgress = createSelector(selectSlice, get('cloneProjectProgress'));
export const selectCloneProjectError = createSelector(selectSlice, get('cloneProjectError'));

export const isCurrentProject = (spaceUid, state) => {
    const space = selectProject(state);
    return spaceUid === space?.uid;
};

const selectRecentlyOpenedProjectsByUser = createSelector(
    selectSlice,
    get('recentlyOpenedProjectsByUser')
);

export const selectRecentProjectsForCurrentUser = createSelector(
    selectRecentlyOpenedProjectsByUser,
    selectCurrentUserName,
    (projects, user) => projects[user] ?? []
);

export const selectSlugForPropEntityFqn = createSelector(
    selectEntitiesById,
    (_, props) => props,
    (entities, { fqn, spaceUid }) => {
        const { uid, spaceSlug } = findViewBySpaceAndFqn(entities, spaceUid, fqn);
        return { uid, spaceSlug };
    }
);

const selectProjectInputs = createSelector(selectSlice, get('projectInputs'));
export const selectIsLoadingProjectInputs = createSelector(
    selectSlice,
    get('projectInputs.isLoading')
);
export const selectShouldGetProjectInputsForCurrentProject = createSelector(
    selectPropEntityId,
    selectProjectInputs,
    (projectUid, projectInputs) =>
        !isNil(projectUid) &&
        !projectInputs.isLoading &&
        !(projectUid in projectInputs.byProjectUid)
);

export const selectProjectInputsForPropEntityId = createSelector(
    selectPropEntityId,
    selectProjectInputs,
    (projectUid, projectInputs) => projectInputs.byProjectUid[projectUid]
);

export const selectSelectedFqn = createSelector(selectSlice, get('selectedFqn'));
